import React, { useMemo } from "react";

import AntdPagination from "antd/es/pagination";
import cn from "classnames";
import { useIntl } from "react-intl";

import SelectField from "@app/component/input/SelectField";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";

import styles from "./Pagination.module.scss";

const DEFAULT_ALLOWED_PAGE_SIZES = [100, 250, 500, 1000];

const JUMPER_THRESHOLD = 25; // show quick jumper if more than 25 pages

interface Props {
  allowedPageSizes?: number[];
  className?: string;
  currentPage: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSize?: number;
  totalRecords: number;
}

const Pagination: React.FC<Props> = ({
  allowedPageSizes = DEFAULT_ALLOWED_PAGE_SIZES,
  className,
  currentPage,
  onChange,
  onPageSizeChange,
  pageSize = DEFAULT_PAGE_SIZE,
  totalRecords,
}) => {
  const intl = useIntl();
  const pageSizeOptions = useMemo(
    () =>
      allowedPageSizes.map((value) => ({
        label: intl.formatMessage(
          {
            id: "grid.pagination.pageSizeSelector.option",
            defaultMessage:
              "{rowsPerPage} {rowsPerPage, plural, one {row per page} other {rows per page}}",
            description: "Page size option label",
          },
          { rowsPerPage: value }
        ),
        value,
      })),
    [allowedPageSizes, intl]
  );

  const showQuickJumper = totalRecords / pageSize >= JUMPER_THRESHOLD;

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.pageSize}>
        {onPageSizeChange && (
          <SelectField<number>
            dropdownMatchSelectWidth={false}
            onChange={onPageSizeChange}
            options={pageSizeOptions}
            value={pageSize}
          />
        )}
      </div>
      <div className={styles.pagination}>
        <AntdPagination
          current={currentPage}
          onChange={onChange}
          pageSize={pageSize}
          showQuickJumper={showQuickJumper}
          showSizeChanger={false}
          total={totalRecords}
        />
      </div>
      <div>{/* dummy div for the 3rd column */}</div>
    </div>
  );
};

export default Pagination;
